import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api } from '../lib/api'

const fmt = (n: number): string => (n < 1000 ? `${n}` : `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`)

/**
 * Per-bid AI token consumption, aggregated across ALL users and AI features (chat, improve, SVG,
 * ask, embeddings). Persisted server-side; polled so it updates live as agents run.
 *  - `compact`: a one-line badge (left nav).
 *  - `card`:    a stat block with the per-model breakdown (bid overview).
 */
export function BidUsage({ bid, variant }: { bid: string | null; variant: 'compact' | 'card' }) {
  const { t } = useLang()
  const q = useQuery({
    queryKey: ['cost', bid],
    queryFn: () => api.bidCost(bid as string),
    enabled: Boolean(bid),
    refetchInterval: 5000,
  })
  const data = q.data
  const totalTokens = (data?.byModel ?? []).reduce((s, m) => s + m.tokens, 0)
  const cost = (data?.spendTotal ?? 0) + (data?.effortTotal ?? 0)

  if (variant === 'compact') {
    return (
      <div
        className="bidusage-compact"
        title={`${totalTokens.toLocaleString()} ${t('tokens')} · $${cost.toFixed(4)} — ${t('usage_all_users')}`}
      >
        <span aria-hidden="true">⨋</span> {fmt(totalTokens)} {t('tokens')}
        <span className="bidusage-cost"> · ${cost.toFixed(2)}</span>
      </div>
    )
  }

  const models = (data?.byModel ?? []).filter((m) => m.tokens > 0).sort((a, b) => b.tokens - a.tokens)
  return (
    <section className="bidusage-card" aria-label={t('usage_title')}>
      <div className="bidusage-head">
        <span className="home-eyebrow">{t('usage_title')}</span>
        <span className="bidusage-sub">{t('usage_all_users')}</span>
      </div>
      <div className="bidusage-main">
        <div className="bidusage-big">
          <strong>{totalTokens.toLocaleString()}</strong> {t('tokens')}
        </div>
        {cost > 0 && <div className="bidusage-money">${cost.toFixed(2)} {data?.currency ?? 'USD'}</div>}
      </div>
      {models.length > 0 && (
        <ul className="bidusage-models">
          {models.map((m) => (
            <li key={`${m.model}|${m.category}`}>
              <span className="lbl">{m.model}</span>
              <span className="tag">{m.category}</span>
              <span className="val">
                {m.tokens.toLocaleString()} {t('tokens')}
                {m.cost > 0 && <span className="bidusage-cost"> · ${m.cost.toFixed(2)}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
